import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAllTagsAsync, createTagAsync, updateTagAsync, deleteTagAsync } from "../redux/tag";
import { useFooter } from "../components/layout/FooterContext";
import { Trash, SquarePen, Plus, Check, X } from "lucide-react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  useDisclosure,
  addToast,
} from "@heroui/react";
import AppButton from "../components/common/AppButton";

const Tags = () => {
  const dispatch = useDispatch();
  const { tags, loading } = useSelector((state) => state.tags);
  const { setFooterContent, setTitle } = useFooter();

  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [newTagName, setNewTagName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { isOpen: isCreateOpen, onOpen: openCreate, onOpenChange: onCreateChange } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: openDelete, onOpenChange: onDeleteChange } = useDisclosure();

  useEffect(() => {
    dispatch(getAllTagsAsync());
  }, [dispatch]);

  useEffect(() => {
    setTitle("Tags");
    setFooterContent(null);
    return () => setFooterContent(null);
  }, [setTitle, setFooterContent]);

  const handleCreate = async (onClose) => {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    try {
      await dispatch(createTagAsync({ name: trimmed })).unwrap();
      addToast({ title: "Tag erstellt", color: "success" });
      setNewTagName("");
      onClose();
    } catch (err) {
      addToast({ title: "Tag konnte nicht erstellt werden", color: "danger" });
    }
  };

  const startEdit = (tag) => {
    setEditingId(tag.id);
    setEditingName(tag.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = async () => {
    const trimmed = editingName.trim();
    if (!trimmed || editingId == null) return;
    try {
      await dispatch(updateTagAsync({ id: editingId, payload: { name: trimmed } })).unwrap();
      addToast({ title: "Tag aktualisiert", color: "success" });
      cancelEdit();
    } catch (err) {
      addToast({ title: "Tag konnte nicht aktualisiert werden", color: "danger" });
    }
  };

  const confirmDelete = (tag) => {
    setDeleteTarget(tag);
    openDelete();
  };

  const handleDelete = async (onClose) => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteTagAsync(deleteTarget.id)).unwrap();
      addToast({ title: "Tag gelöscht", color: "success" });
    } catch (err) {
      addToast({ title: "Tag konnte nicht gelöscht werden", color: "danger" });
    }
    setDeleteTarget(null);
    onClose();
  };

  return (
    <div className="px-4">
      <div className="h-[82vh] bg-surface rounded-2xl p-3">
        <div className="flex flex-row gap-4 h-full min-h-0">

          {/* Left Panel */}
          <div className="w-64 shrink-0 flex flex-col gap-2">
            <p className="text-xs text-default-400 uppercase font-semibold px-3 mb-1">
              Tags
            </p>
            <p className="text-xs text-default-400 px-3 mb-3">
              Hier können Sie Tags verwalten, erstellen und löschen.
            </p>
            <div className="px-3">
              <AppButton onClick={openCreate} width="w-full" className="gap-1">
                <Plus className="w-4 h-4" />
                Neuer Tag
              </AppButton>
            </div>
          </div>

          {/* Divider */}
          <div className="w-px bg-surfaceContainer shrink-0" />

          {/* Right Panel - Tag list */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {loading && <p className="text-sm text-default-500 p-4">Laden...</p>}
            {!loading && tags.length === 0 && (
              <div className="flex items-center justify-center h-full text-sm text-default-400">
                Noch keine Tags vorhanden.
              </div>
            )}
            <div className="flex flex-col gap-2 p-2">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 shadow-sm"
                >
                  {editingId === tag.id ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="text"
                        className="flex-1 bg-transparent text-sm text-onSurface focus:outline-none border-b border-gray-300"
                        value={editingName}
                        autoFocus
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit();
                          else if (e.key === "Escape") cancelEdit();
                        }}
                      />
                      <Button
                        isIconOnly
                        size="sm"
                        className="rounded-full bg-secondaryContainer text-onSecondaryContainer hover:bg-secondary hover:text-onSecondary"
                        onPress={saveEdit}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        className="rounded-full bg-errorContainer text-onErrorContainer hover:bg-error hover:text-onError"
                        onPress={cancelEdit}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm font-medium text-onSurface">{tag.name}</span>
                      <div className="flex items-center gap-1">
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          onPress={() => startEdit(tag)}
                          aria-label="Tag bearbeiten"
                        >
                          <SquarePen className="w-4 h-4" />
                        </Button>
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          color="danger"
                          onPress={() => confirmDelete(tag)}
                          aria-label="Tag löschen"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <Modal isOpen={isCreateOpen} onOpenChange={onCreateChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Neuer Tag</ModalHeader>
              <ModalBody>
                <Input
                  label="Name"
                  placeholder="z.B. Wichtig"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreate(onClose);
                  }}
                />
              </ModalBody>
              <ModalFooter>
                <Button variant="bordered" radius="full" onPress={onClose}>
                  Abbrechen
                </Button>
                <AppButton width="w-[120px]" onClick={() => handleCreate(onClose)}>
                  Erstellen
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal isOpen={isDeleteOpen} onOpenChange={onDeleteChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Tag löschen?</ModalHeader>
              <ModalBody>
                <p className="text-sm text-onSurface/70">
                  Der Tag <strong>{deleteTarget?.name}</strong> wird dauerhaft gelöscht
                  und von allen Sitzungen entfernt. Dieser Vorgang kann nicht rückgängig gemacht werden.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="bordered" radius="full" onPress={onClose}>
                  Abbrechen
                </Button>
                <Button color="danger" onPress={() => handleDelete(onClose)}>
                  Löschen
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default Tags;
